import { computed, inject, InjectionToken } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { patchState, signalStore, withComputed, withState } from '@ngrx/signals';
import { Events, on, withEventHandlers, withReducer } from '@ngrx/signals/events';
import { catchError, EMPTY, exhaustMap, expand, last, map, Observable, of, switchMap, takeWhile, tap, timer } from 'rxjs';
import { ReviewExportData, ReviewExportStatus } from '@models/online-reputation.models';
import { ReviewsService } from '@core/services/reviews.service';
import { withLogger } from '@core/stores/features/logger.feature';
import { ReviewExportError, reviewExportApiEvents, startReviewExport } from '@core/events/review-export.events';

/**
 * Starts the browser download of a finished export's signed link. A plain anchor navigation: the
 * link needs no auth header, and the storage response's attachment disposition keeps the user on
 * the page. A token so tests can observe it instead of navigating.
 */
export const REVIEW_EXPORT_DOWNLOAD = new InjectionToken<(url: string) => void>('REVIEW_EXPORT_DOWNLOAD', {
  providedIn: 'root',
  factory: () => (url: string) => {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.rel = 'noopener';
    anchor.click();
  },
});

/** Delay before each status check: quick at first, then backing off for long exports. */
export const REVIEW_EXPORT_POLL_DELAYS_MS = [2000, 5000, 10000];

const TERMINAL: ReadonlyArray<ReviewExportStatus | undefined> = [
  ReviewExportStatus.COMPLETED, ReviewExportStatus.FAILED, ReviewExportStatus.EXPIRED,
];

export type ReviewExportState = {
  // true from the click until the download starts or the export fails
  inProgress: boolean;
  exportId: string | undefined;
  status: ReviewExportStatus | undefined;
  reviewsWritten: number;
  totalReviews: number | undefined;
  error: ReviewExportError | undefined;
};

export const initialReviewExportState: ReviewExportState = {
  inProgress: false,
  exportId: undefined,
  status: undefined,
  reviewsWritten: 0,
  totalReviews: undefined,
  error: undefined,
};

function pollDelay(attempt: number): number {
  return REVIEW_EXPORT_POLL_DELAYS_MS[Math.min(attempt, REVIEW_EXPORT_POLL_DELAYS_MS.length - 1)];
}

/**
 * Drives one reviews Excel export at a time: starts it, polls its status with backoff until it
 * finishes, then fetches the signed link and starts the download. A start while one is already in
 * progress is ignored (exhaustMap), so a double click never starts a second export.
 */
export const ReviewExportStore = signalStore(
  { providedIn: 'root' },
  withState<ReviewExportState>(initialReviewExportState),
  withLogger('ReviewExportStore'),
  withComputed(({ reviewsWritten, totalReviews }) => ({
    // 0-100 once the total is known, undefined before (show an indeterminate bar)
    percent: computed(() => {
      const total = totalReviews();
      return total ? Math.min(100, Math.round(reviewsWritten() * 100 / total)) : undefined;
    }),
  })),
  withReducer(
    on(startReviewExport, () => ({ ...initialReviewExportState, inProgress: true })),
    on(reviewExportApiEvents.downloaded, ({ payload }) => ({ inProgress: false, status: payload.status })),
    on(reviewExportApiEvents.failed, ({ payload: error }) => ({ inProgress: false, error })),
  ),
  withEventHandlers(
    (
      store,
      events = inject(Events),
      reviewsService = inject(ReviewsService),
      download = inject(REVIEW_EXPORT_DOWNLOAD),
    ) => {
      const poll = (id: string): Observable<ReviewExportData> =>
        timer(pollDelay(0)).pipe(
          switchMap(() => reviewsService.getExport(id)),
          expand((exportData, attempt) => TERMINAL.includes(exportData.status)
            ? EMPTY
            : timer(pollDelay(attempt + 1)).pipe(switchMap(() => reviewsService.getExport(id)))),
          tap((exportData) => patchState(store, {
            status: exportData.status,
            reviewsWritten: exportData.reviewsWritten ?? 0,
            totalReviews: exportData.totalReviews,
          })),
          takeWhile((exportData) => !TERMINAL.includes(exportData.status), true),
        );

      return {
        export$: events.on(startReviewExport).pipe(
          exhaustMap(({ payload }) => reviewsService.startExport(payload).pipe(
            tap((started) => patchState(store, { exportId: started.id, status: started.status })),
            switchMap((started) => poll(started.id!)),
            last(),
            switchMap((finished) => finished.status === ReviewExportStatus.COMPLETED
              ? reviewsService.getExportDownload(finished.id!).pipe(
                tap((link) => download(link.url!)),
                map(() => reviewExportApiEvents.downloaded(finished)))
              : of(reviewExportApiEvents.failed('failed'))),
            catchError((error: unknown) => of(reviewExportApiEvents.failed(
              error instanceof HttpErrorResponse && error.status === 429 ? 'tooMany' : 'failed'))),
          )),
        ),
      };
    },
  ),
);
